import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { homeApi } from '../api/api';
import SocialLink from './ui/SocialLink';
import { DEFAULT_SOCIAL_URLS } from '../utils/socialLinks';

const EMPTY_CONTACT = {
  phone: '',
  email: '',
};

const DEFAULT_SOCIAL = [
  { id: 'facebook', key: 'social_facebook', icon: 'fab fa-facebook-f', url: DEFAULT_SOCIAL_URLS.facebook, labelKey: 'home.social.facebook.title' },
  { id: 'instagram', key: 'social_instagram', icon: 'fab fa-instagram', url: DEFAULT_SOCIAL_URLS.instagram, labelKey: 'home.social.instagram.title' },
  { id: 'youtube', key: 'social_youtube', icon: 'fab fa-youtube', url: DEFAULT_SOCIAL_URLS.youtube, labelKey: 'home.social.youtube.title' },
];

function firstNonEmpty(...values) {
  for (const v of values) {
    const s = v != null ? String(v).trim() : '';
    if (s) return s;
  }
  return '';
}

function parseOfficeFromBody(body) {
  if (!body) return '';
  const line = String(body).replace(/\r\n/g, '\n').split('\n').find((l) =>
    /^ashram office\s*:/i.test(l.trim())
  );
  if (!line) return '';
  return line.replace(/^[^:]+:\s*/, '').trim();
}

function parseGeneralFromBody(body) {
  if (!body) return '';
  const line = String(body).replace(/\r\n/g, '\n').split('\n').find((l) =>
    /^general\s*:/i.test(l.trim())
  );
  if (!line) return '';
  return line.replace(/^[^:]+:\s*/, '').trim();
}

const Footer = () => {
  const { t } = useTranslation();
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [socialLinks, setSocialLinks] = useState(DEFAULT_SOCIAL);

  useEffect(() => {
    homeApi
      .getAll()
      .then((res) => {
        if (!res.success || !res.data) {
          setContact(EMPTY_CONTACT);
          return;
        }
        const d = res.data;
        const phonesRaw = d.ashram_phones;
        const emailsRaw = d.ashram_emails;

        const phone = firstNonEmpty(
          phonesRaw && phonesRaw.ashramOffice,
          phonesRaw && parseOfficeFromBody(phonesRaw.body)
        );

        const email = firstNonEmpty(
          emailsRaw && emailsRaw.general,
          emailsRaw && parseGeneralFromBody(emailsRaw.body)
        );

        setContact({ phone, email });
        setSocialLinks(
          DEFAULT_SOCIAL.map((item) => {
            const raw = d[item.key];
            const url = raw && typeof raw === 'object' && (raw.url || '').trim()
              ? String(raw.url).trim()
              : item.url;
            return { ...item, url };
          })
        );
      })
      .catch(() => {
        setContact(EMPTY_CONTACT);
      });
  }, []);

  const addressLines = t('footer.default_address').split('\n').filter((l) => l.trim() !== '');

  return (
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <img
                src="/logo.jpg"
                alt={t('header.logo_alt')}
                className="h-12 w-12 mr-3 rounded-full object-cover border border-orange-400"
              />
              <div>
                <h3 className="text-xl font-bold text-orange-400">{t('header.site_name')}</h3>
                <p className="text-xs text-gray-400">{t('header.site_tagline')}</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <SocialLink
                  key={item.id}
                  href={item.url}
                  aria-label={t(item.labelKey)}
                  className="text-gray-300 hover:text-orange-400 transition-colors duration-200"
                >
                  <i className={item.icon}></i>
                </SocialLink>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.sacred_pathways')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/shree-radhe-krishna-bapu" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.events')}
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.gallery')}
                </Link>
              </li>
              <li>
                <Link to="/donate" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.seva_donation')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.spiritual_resources')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/daily-message" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('footer.divine_teachings')}
                </Link>
              </li>
              <li>
                <Link to="/katha" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.katha')}
                </Link>
              </li>
              <li>
                <Link to="/visiting" className="text-gray-300 hover:text-orange-400 transition-colors duration-200">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.contact_info')}</h4>
            <div className="space-y-2 text-gray-300">
              <p className="flex items-start">
                <i className="fas fa-map-marker-alt mr-2 mt-1"></i>
                <span>
                  {addressLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < addressLines.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </span>
              </p>
              {contact.phone ? (
                <p className="flex items-center">
                  <i className="fas fa-phone mr-2"></i>
                  {contact.phone}
                </p>
              ) : null}
              {contact.email ? (
                <p className="flex items-center">
                  <i className="fas fa-envelope mr-2"></i>
                  {contact.email}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-300">
            {t('footer.copyright', { year: 2026 })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
